import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["type"],
  _excluded2 = ["_onTestContextReady"];
import { PureComponent } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { ERROR_EMBED_CONTEXT_INVALID_RECEIVE_MESSAGE, ERROR_INVALID_CALLBACK_PROVIDED } from '../messaging/Errors';
import { createEmbeddedContext, createReadyMessage, createSyncAckMessage, isEmbeddedPropsChangedMessage, isSyncMessage, validMessage } from '../messaging/IFrameControlMessage';
import { isMessageToMe, validFormat } from '../messaging/IFrameMessage';
import { getEmbeddedPropsFromQueryParams, getTopLevelEmbeddedContextDefaults, isTopLevelWindowSupported } from '../utils/Embedded';
import { logEmbedError, logEmbedMessageReceived, logEmbedMessageSent } from '../utils/Logger';
import { jsx as _jsx } from "react/jsx-runtime";
// To allow your EmbeddedComponent to render outside of an iframe:
// set `enableTopLevelEmbed` to render in ALL environments including prod (ensure this will not have adverse consequences!)
// set `enableTopLevelEmbedForTestOnly` to render ONLY in the QA environment
const UIEmbeddedComponentHOC = (EmbeddedComponent, {
  enableTopLevelEmbed = false,
  enableTopLevelEmbedForTestOnly = false,
  allowedOrigins
} = {}) => {
  class UIEmbeddedComponent extends PureComponent {
    constructor(...args) {
      super(...args);
      this.callbacks = new Map();
      this.parent = {
        origin: null
      };
      this.child = {
        embeddedContext: null,
        receiveMessage: emptyFunction
      };
      this.state = {
        embeddedProps: {},
        synced: false
      };
      this.createEmbeddedContext = ({
        embedId,
        group,
        name,
        appData
      }, sendMessageFn = this.sendMessage) => {
        return createEmbeddedContext({
          embedId,
          group,
          name,
          app: appData,
          sendMessageFn
        });
      };
      this.initEmbeddedContext = message => {
        this.child.embeddedContext = this.createEmbeddedContext(message.payload);
      };
      this.initWhenTopLevel = () => {
        this.child.embeddedContext = this.createEmbeddedContext(getTopLevelEmbeddedContextDefaults(), emptyFunction);
        this.setState({
          synced: true,
          embeddedProps: getEmbeddedPropsFromQueryParams()
        });
      };
      this.validMessage = event => {
        if (!this.parent.origin) {
          if (validFormat(event.data) && (!allowedOrigins || allowedOrigins.includes(event.origin))) {
            this.parent.origin = event.origin;
          }
        }
        return validMessage(event, this.parent.origin);
      };
      this.isToEmbeddedComponent = message => {
        return isMessageToMe(message, this.child.embeddedContext.id, this.child.embeddedContext.group);
      };
      this.hasCallback = message => {
        return this.callbacks.has(message.id);
      };
      this.validCallback = callback => {
        if (typeof callback === 'function') {
          return true;
        }
        logEmbedError(this.child.embeddedContext.name, ERROR_INVALID_CALLBACK_PROVIDED, JSON.stringify(callback));
        return false;
      };
      this.dispatchCallback = message => {
        const callback = this.callbacks.get(message.id);
        if (typeof callback === 'function') {
          callback(message.payload);
        }
        this.callbacks.delete(message.id);
      };
      this.sendMessage = (message, callback) => {
        if (callback && this.validCallback(callback)) {
          this.callbacks.set(message.id, callback);
        }
        window.parent.postMessage(message, this.parent.origin);
        logEmbedMessageSent(this.child.embeddedContext.name, message);
      };
      this._simulatePostMessage = event => {
        this.handleReceiveMessage(event);
      };
      this.logMessageReceived = message => {
        if (!this.child.embeddedContext) {
          return;
        }
        logEmbedMessageReceived(this.child.embeddedContext.name, message);
      };
      this.handleOnReady = childContext => {
        if (typeof childContext.receiveMessage !== 'function') {
          logEmbedError(this.child.embeddedContext.name, ERROR_EMBED_CONTEXT_INVALID_RECEIVE_MESSAGE, childContext);
          return;
        }
        this.child.receiveMessage = childContext.receiveMessage;
        const readyMessage = createReadyMessage(this.child.embeddedContext.id, childContext.data);
        this.sendMessage(readyMessage);
      };
      this.handleSyncMessage = message => {
        if (this.state.synced) {
          return;
        }
        this.sendMessage(createSyncAckMessage(message));
        this.setState({
          synced: true,
          embeddedProps: message.payload.embeddedProps
        });
      };
      this.handleEmbeddedPropsChangedMessage = message => {
        const _message$payload = message.payload,
          embeddedProps = _objectWithoutPropertiesLoose(_message$payload, _excluded);
        this.setState({
          embeddedProps
        });
      };
      this.handleReceiveMessage = event => {
        if (!this.validMessage(event)) {
          return;
        }
        const {
          data: message
        } = event;
        if (isSyncMessage(message) && !this.state.synced) {
          this.initEmbeddedContext(message);
        }
        this.logMessageReceived(message);
        if (isSyncMessage(message)) {
          this.handleSyncMessage(message);
        } else if (isEmbeddedPropsChangedMessage(message)) {
          this.handleEmbeddedPropsChangedMessage(message);
        } else if (this.state.synced) {
          if (this.hasCallback(message)) {
            this.dispatchCallback(message);
          } else if (this.isToEmbeddedComponent(message)) {
            this.child.receiveMessage(message);
          }
        }
      };
    }
    componentDidMount() {
      this.props._onTestContextReady({
        simulatePostMessage: this._simulatePostMessage
      });
      window.addEventListener('message', this.handleReceiveMessage, false);
      if (isTopLevelWindowSupported({
        enableTopLevelEmbed,
        enableTopLevelEmbedForTestOnly
      })) {
        this.initWhenTopLevel();
      }
    }
    componentWillUnmount() {
      window.removeEventListener('message', this.handleReceiveMessage);
    }

    // used for testing only

    render() {
      if (!this.state.synced) {
        return null;
      }
      const _this$props = this.props,
        rest = _objectWithoutPropertiesLoose(_this$props, _excluded2);
      return /*#__PURE__*/_jsx(EmbeddedComponent, Object.assign({}, rest, this.state.embeddedProps, {
        onReady: this.handleOnReady,
        embeddedContext: this.child.embeddedContext
      }));
    }
  }
  UIEmbeddedComponent.defaultProps = {
    _onTestContextReady: emptyFunction
  };
  UIEmbeddedComponent.EmbeddedComponent = EmbeddedComponent;
  UIEmbeddedComponent.displayName = 'UIEmbeddedComponent';
  return UIEmbeddedComponent;
};
export { UIEmbeddedComponentHOC as UIEmbeddedComponent };
export default UIEmbeddedComponentHOC;